import React from 'react';
import SEO from '../components/shared/SEO';

export default function WeddingWebsiteShowcasePage({ navigateTo, user }) {
    const handleGetStarted = () => {
        if (user && !user.isAnonymous) {
            navigateTo('weddingDashboard');
        } else {
            navigateTo('login');
        }
    };

    const features = [
        { icon: '📖', title: 'Our Story', desc: 'A beautiful timeline block to share how you met and your favorite memories.' },
        { icon: '📅', title: 'Event Details', desc: 'Integrated maps and calendars so guests never miss the rehearsal or the big day.' },
        { icon: '🎁', title: 'Gift Registry', desc: 'Directly link your favorite registry sites in a clean, accessible layout.' },
        { icon: '👥', title: 'Guest Management', desc: 'Collect RSVPs, meal preferences, and song requests for the reception.' },
        { icon: '📸', title: 'Photo Gallery', desc: 'A dedicated, high-resolution gallery to showcase your engagement photos.' },
        { icon: '✨', title: 'Custom Themes', desc: 'Select from elegant, romantic, or modern designs that match your wedding aesthetic.' }
    ];

    return (
        <div className="min-h-screen bg-white font-sans animate-fade-in pb-20">
            <SEO title="Beautiful Wedding Websites" description="Create a stunning, personalized wedding website with Host-it! featuring story timelines, gift registries, and seamless RSVP management." />

            {/* Hero Section */}
            <section className="relative h-[85vh] w-full flex items-center justify-center overflow-hidden px-6">
                {/* Immersive Background */}
                <div className="absolute inset-0">
                    <img 
                        src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=80" 
                        alt="Elegant Wedding Background" 
                        className="w-full h-full object-cover"
                    />
                    {/* Soft Spotlight Overlay: darker edges, clear center for focus */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.1)_0%,rgba(0,0,0,0.6)_100%)]"></div>
                </div>
                
                <div className="relative z-10 max-w-4xl mx-auto text-center animate-fade-in-up">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-amber-200 text-xs font-bold uppercase tracking-widest mb-6 shadow-sm">
                        <span className="text-xl leading-none">💍</span> Elegant Wedding Websites
                    </span>
                    <h1 className="text-5xl md:text-8xl font-extrabold font-serif leading-[0.95] mb-8 tracking-tighter text-white drop-shadow-2xl">
                        Your day, <br/> <span className="text-amber-400 italic">your story.</span>
                    </h1>
                    <p className="text-lg md:text-2xl text-stone-100 max-w-2xl mx-auto mb-10 leading-relaxed font-light drop-shadow-md">
                        Build a timeless digital keepsake to share your journey, collect RSVPs, and coordinate all the beautiful details of your wedding.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <button 
                            onClick={() => navigateTo('login')}
                            className="bg-white hover:bg-stone-100 text-stone-900 px-10 py-5 rounded-full font-bold text-lg transition-all shadow-[0_10px_40px_-10px_rgba(255,255,255,0.3)] hover:scale-105 transform"
                        >
                            Design Your Wedding Site
                        </button>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="max-w-7xl mx-auto py-24 px-6">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-stone-900 font-serif mb-4">Everything for your big day.</h2>
                    <p className="text-lg text-stone-500">Elegant tools designed to make planning your wedding seamless.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((f, i) => (
                        <div key={i} className="p-8 rounded-3xl bg-stone-50 border border-stone-100 hover:shadow-lg hover:border-amber-200 transition-all duration-300">
                            <div className="text-4xl mb-6">{f.icon}</div>
                            <h3 className="text-xl font-bold text-stone-900 mb-3">{f.title}</h3>
                            <p className="text-stone-600 leading-relaxed text-sm">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Visual Preview Section */}
            <section className="py-24 bg-stone-100">
                <div className="container mx-auto px-6 max-w-5xl text-center">
                    <h2 className="text-4xl font-bold font-serif mb-16">Sophisticated Designs</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white p-2 rounded-2xl shadow-xl rotate-[-2deg] hover:rotate-0 transition duration-500">
                            <img src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=800&q=80" alt="Theme 1" className="rounded-xl w-full h-80 object-cover" />
                        </div>
                        <div className="bg-white p-2 rounded-2xl shadow-xl rotate-[2deg] hover:rotate-0 transition duration-500 mt-8 md:mt-0">
                            <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=800&q=80" alt="Theme 2" className="rounded-xl w-full h-80 object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-32 bg-stone-900 text-white text-center px-6">
                <h2 className="text-4xl md:text-5xl font-bold font-serif mb-6">Start your wedding journey.</h2>
                <p className="text-xl text-stone-400 mb-10 max-w-2xl mx-auto">Get your custom wedding link today and start sharing your story with your loved ones.</p>
                <button 
                    onClick={handleGetStarted}
                    className="bg-amber-500 hover:bg-amber-600 text-white px-12 py-4 rounded-full font-bold text-lg transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                >
                    Get Started Free
                </button>
            </section>
        </div>
    );
}